import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import useSWR from 'swr';

import { IBuyBarProductVariant } from 'shared/types/BuyBarProductVariant';
import { requestService } from 'shared/utils/services/requestService';
import { getBoxConditionCopy } from 'productTemplate/utils/getBoxConditionCopy';
import { getWindowItemProductVariantRowPriceLength } from 'productTemplate/utils/getWindowItemProductVariantRowPriceLength';
import { useProductTemplate } from 'productTemplate/hooks/useProductTemplate';
import { WindowItemProductVariantRow } from 'shared/components';

const fetchData = (url) => requestService({ url, method: 'GET' });

interface IWindowItemProductVariantRowsProps {
  bestPriceVariant?: IBuyBarProductVariant;
  instantShipVariant?: IBuyBarProductVariant;
  newWithBoxIssuesVariant?: IBuyBarProductVariant;
  showNewWithBoxIssues?: boolean;
}

export const WindowItemProductVariantRows = ({
  bestPriceVariant,
  instantShipVariant,
  newWithBoxIssuesVariant,
  showNewWithBoxIssues = false,
}: IWindowItemProductVariantRowsProps) => {
  const { t } = useTranslation();
  const { id, isFashionProduct, localizedSpecialDisplayPriceCents, productType } =
    useProductTemplate();
  const isSneaker = productType === 'sneakers';
  const { data: descriptionCopy } = useSWR(
    `/web-api/v1/product_variants/buy_bar_copies?productTemplateId=${id}`,
    fetchData,
  );

  const instantShipPriceIsLowestPrice =
    bestPriceVariant?.lowestPriceCents?.amount ===
    instantShipVariant?.instantShipLowestPriceCents?.amount;
  const isUnderRetail =
    bestPriceVariant?.lowestPriceCents?.amount < localizedSpecialDisplayPriceCents?.amount;

  // calculates the width of the price div according to length
  const productPriceLength = getWindowItemProductVariantRowPriceLength(
    bestPriceVariant?.lowestPriceCents,
  );
  const instantShipPriceLength = getWindowItemProductVariantRowPriceLength(
    instantShipVariant?.instantShipLowestPriceCents,
  );
  const newWithDefectsPriceLength = getWindowItemProductVariantRowPriceLength(
    newWithBoxIssuesVariant?.lowestPriceCents,
  );
  const longestPriceLength = Math.max(
    productPriceLength,
    instantShipPriceLength,
    newWithDefectsPriceLength,
  );

  const getCondition = () => {
    if (isUnderRetail) {
      return isSneaker
        ? t('productTemplate.underRetail', 'Under Retail')
        : t('productTemplate.sale', 'Sale');
    }
    return t('productTemplate.bestPrice', 'Best Price');
  };

  return (
    <WindowItemProductVariantRows.Wrapper>
      {bestPriceVariant && !instantShipPriceIsLowestPrice && (
        <WindowItemProductVariantRow
          condition={getCondition()}
          description={!isFashionProduct && descriptionCopy?.web?.best}
          isBestPrice
          isUnderRetail={isUnderRetail}
          longestPriceLength={longestPriceLength}
          variant={bestPriceVariant}
        />
      )}
      {instantShipVariant && (
        <WindowItemProductVariantRow
          condition={t('productTemplate.instant', 'Instant')}
          description={descriptionCopy?.web?.instant}
          isInstant
          isUnderRetail={isUnderRetail}
          longestPriceLength={longestPriceLength}
          variant={instantShipVariant}
        />
      )}
      {showNewWithBoxIssues && newWithBoxIssuesVariant && (
        <WindowItemProductVariantRow
          condition={getBoxConditionCopy(newWithBoxIssuesVariant?.boxCondition)}
          description={descriptionCopy?.web?.best}
          isUnderRetail={isUnderRetail}
          longestPriceLength={longestPriceLength}
          variant={newWithBoxIssuesVariant}
        />
      )}
    </WindowItemProductVariantRows.Wrapper>
  );
};

WindowItemProductVariantRows.Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  a:last-child {
    margin-bottom: 0;
  }
`;
